<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

import { presentationsApi } from '../api'
import AppShell from '../components/AppShell.vue'
import ThemeGallery from '../components/ThemeGallery.vue'
import { navigate } from '../router'
import { notify } from '../toasts'
import type { Aspect, PresetThemeId, Theme } from '../types'

defineEmits<{
  toggleTheme: []
}>()

const description = ref('')
const aspect = ref<Aspect>('16:9')
const themeId = ref<PresetThemeId | 'auto'>('clean')
const slideCount = ref(8)
const audience = ref('')
const tone = ref('')
const busy = ref(false)
const error = ref('')
const createdId = ref('')
const progressIndex = ref(0)
let progressTimer = 0

const progressMessages = [
  'Выделяем главную мысль',
  'Собираем драматургию',
  'Пишем коротко и живо',
  'Подбираем композицию',
  'Проверяем каждый слайд',
]

const canSubmit = computed(() => description.value.trim().length >= 12 && !busy.value)

function suggestedTitle(): string {
  const first = description.value
    .trim()
    .split(/\n|[.!?](?:\s|$)/u)
    .find(Boolean)
    ?.trim() ?? 'Новая презентация'
  return first.slice(0, 150)
}

function startProgress(): void {
  window.clearInterval(progressTimer)
  progressIndex.value = 0
  progressTimer = window.setInterval(() => {
    progressIndex.value = Math.min(progressIndex.value + 1, progressMessages.length - 1)
  }, 4_500)
}

async function create(): Promise<void> {
  if (!canSubmit.value) return
  busy.value = true
  error.value = ''
  startProgress()
  try {
    let presentationId: string
    let baseRevision: number
    if (createdId.value) {
      const existing = await presentationsApi.get(createdId.value)
      presentationId = existing.presentation.id
      baseRevision = existing.revision.number
    } else {
      const initialTheme: Theme = themeId.value === 'auto'
        ? { id: 'clean' }
        : { id: themeId.value }
      const created = await presentationsApi.create({
        title: suggestedTitle(),
        aspect: aspect.value,
        theme: initialTheme,
      })
      createdId.value = created.presentation.id
      presentationId = created.presentation.id
      baseRevision = created.revision.number
    }

    await presentationsApi.generate(presentationId, {
      baseRevision,
      description: description.value.trim(),
      slideCount: slideCount.value,
      theme: themeId.value === 'auto'
        ? { id: 'auto', description: description.value.trim() }
        : { id: themeId.value },
      ...(audience.value.trim() ? { audience: audience.value.trim() } : {}),
      ...(tone.value.trim() ? { tone: tone.value.trim() } : {}),
    })
    notify('Презентация готова. Превью дорисуются через несколько секунд.', 'success')
    navigate(`/deck/${presentationId}`, true)
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Не удалось сделать презентацию.'
  } finally {
    busy.value = false
    window.clearInterval(progressTimer)
  }
}

function continueToDraft(): void {
  if (createdId.value) navigate(`/deck/${createdId.value}`)
}

onBeforeUnmount(() => window.clearInterval(progressTimer))
</script>

<template>
  <AppShell compact @toggle-theme="$emit('toggleTheme')">
    <div class="create-layout">
      <section class="create-main">
        <a href="/" class="back-link">← Мои презентации</a>
        <span class="eyebrow">Новая презентация</span>
        <h1>О чём будем рассказывать?</h1>
        <p class="lead">
          Пишите как человеку: цель, аудитория, важные факты. Чем конкретнее мысль,
          тем точнее получится история.
        </p>

        <form class="create-form" @submit.prevent="create">
          <div class="field field--large">
            <label for="deck-description">Описание</label>
            <textarea
              id="deck-description"
              v-model="description"
              rows="7"
              maxlength="8000"
              autofocus
              placeholder="Например: презентация для команды о запуске нового продукта. Хочу объяснить, какую проблему решаем, показать первые цифры и договориться о плане на три месяца…"
            ></textarea>
            <small>{{ description.length }} / 8000</small>
          </div>

          <details class="creation-details">
            <summary>Добавить контекст <span>необязательно</span></summary>
            <div class="split-fields">
              <div class="field">
                <label for="audience">Для кого</label>
                <input id="audience" v-model="audience" type="text" maxlength="500" placeholder="Команда, инвесторы, клиенты">
              </div>
              <div class="field">
                <label for="tone">Как звучать</label>
                <input id="tone" v-model="tone" type="text" maxlength="200" placeholder="Смело, спокойно, с юмором">
              </div>
            </div>
          </details>

          <fieldset class="choice-section">
            <legend>Формат экрана</legend>
            <div class="aspect-options">
              <label v-for="option in (['16:9', '4:3', '9:16'] as Aspect[])" :key="option">
                <input v-model="aspect" type="radio" name="aspect" :value="option">
                <span class="aspect-icon" :class="`aspect-icon--${option.replace(':', '-')}`"></span>
                <strong>{{ option }}</strong>
                <small>{{ option === '16:9' ? 'Экран и проектор' : option === '4:3' ? 'Классика' : 'Телефон и вертикальный экран' }}</small>
              </label>
            </div>
          </fieldset>

          <fieldset class="choice-section">
            <legend>Визуальный характер</legend>
            <ThemeGallery v-model="themeId" include-auto />
          </fieldset>

          <div class="slide-count">
            <div>
              <label for="slide-count">Сколько слайдов</label>
              <small>Обычно 7–10 хватает на короткое выступление</small>
            </div>
            <div class="range-control">
              <input id="slide-count" v-model.number="slideCount" type="range" min="1" max="30" step="1">
              <output>{{ slideCount }}</output>
            </div>
          </div>

          <p v-if="error" class="form-error">
            {{ error }}
            <button v-if="createdId" type="button" class="text-button" @click="continueToDraft">
              Открыть сохранённый черновик
            </button>
          </p>

          <button type="submit" class="button button--primary button--large button--wide" :disabled="!canSubmit">
            <span v-if="busy" class="mini-loader"></span>
            {{ busy ? progressMessages[progressIndex] : createdId ? 'Повторить генерацию' : 'Сделать презентацию' }}
          </button>
          <p v-if="busy" class="generation-hint">
            Не закрывайте страницу. Обычно это занимает меньше минуты.
          </p>
        </form>
      </section>

      <aside class="create-aside">
        <div class="create-aside__sticky">
          <span>Будет внутри</span>
          <ol>
            <li><i>1</i><strong>Связная история</strong><small>Не набор случайных тезисов</small></li>
            <li><i>2</i><strong>Готовый дизайн</strong><small>Все слайды держат один ритм</small></li>
            <li><i>3</i><strong>Режим выступления</strong><small>Телефон станет пультом и суфлёром</small></li>
          </ol>
        </div>
      </aside>
    </div>
  </AppShell>
</template>
